// design/States.dc.html — empty / offline / error / expired, plus "reopen" (not in the mockups:
// visual from the same layout, behaviour from the owner's requirement for INIT_DATA_EXPIRED).
import type { ReactNode } from 'react';
import type { T } from '../i18n/useT.ts';
import { Button } from '../ui/controls.tsx';
import { IlloCloudMoon, IlloExpiredMoon, IlloMoon, IlloOffline } from '../ui/icons.tsx';
import { Header } from './Home.tsx';

export type StateKind = 'empty' | 'offline' | 'error' | 'expired' | 'reopen';

interface Props {
  kind: StateKind;
  title: string;
  body: string;
  cta: string;
  onCta: () => void;
  secondary?: { label: string; onClick: () => void };
  /** Greeting + profile header like the mockup (not on the reopen screen). */
  header?: { name: string; t: T };
  /** Render above everything (used as a layer / fatal screen). */
  overlay?: boolean;
}

const warm = (k: StateKind) => k === 'empty' || k === 'expired' || k === 'reopen';

export function StateScreen({ kind, title, body, cta, onCta, secondary, header, overlay }: Props) {
  let illo: ReactNode;
  if (kind === 'empty' || kind === 'reopen') illo = <IlloMoon />;
  else if (kind === 'offline') illo = <IlloOffline slash="var(--bg)" />;
  else if (kind === 'error') illo = <IlloCloudMoon />;
  else illo = <IlloExpiredMoon />;

  return (
    <main
      role={overlay ? 'dialog' : undefined}
      aria-modal={overlay || undefined}
      aria-labelledby="state-title"
      style={{
        position: overlay ? 'fixed' : 'relative',
        inset: overlay ? 0 : undefined,
        zIndex: overlay ? 30 : undefined,
        minHeight: '100%',
        background: 'var(--bg)',
        boxSizing: 'border-box',
        padding: 'calc(var(--safe-top) + 12px) 16px calc(var(--safe-bottom) + 34px)',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {header && <Header name={header.name} t={header.t} />}
      <div
        style={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          padding: '0 20px 40px',
        }}
      >
        <div
          style={{
            width: 168,
            height: 168,
            borderRadius: 999,
            background: warm(kind) ? 'var(--accent-soft)' : 'var(--fill)',
            color: warm(kind) ? 'var(--accent)' : 'var(--illo-grey)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {illo}
        </div>
        <h2 id="state-title" className="t-title1 balance" style={{ marginTop: 28 }}>
          {title}
        </h2>
        <p className="t-body secondary" style={{ marginTop: 10, textWrap: 'pretty' }}>
          {body}
        </p>
        <Button
          variant={kind === 'expired' || kind === 'reopen' ? 'primary' : 'secondary'}
          compact
          style={{ marginTop: 28 }}
          onClick={onCta}
        >
          {cta}
        </Button>
        {secondary && (
          <Button variant="text" style={{ marginTop: 4 }} onClick={secondary.onClick}>
            {secondary.label}
          </Button>
        )}
      </div>
    </main>
  );
}
